import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Calendar, Trash2, AlertTriangle, Check } from 'lucide-react';
import { user as userApi, auth } from '../api';

const AccountPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await userApi.getProfile();
      setUserData(data.user);
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') {
      return;
    }

    setDeleting(true);
    try {
      await userApi.deleteAccount();
      
      // Clear all cookies and storage
      const cookieNames = [
        'better-auth.session_token',
        'better-auth.session_data', 
        '__session',
        'session'
      ];
      const domains = ['', '.vltryon.com', 'vltryon.com', 'api.vltryon.com'];
      const paths = ['/', ''];
      
      cookieNames.forEach(name => {
        domains.forEach(domain => {
          paths.forEach(path => {
            const domainPart = domain ? `; domain=${domain}` : '';
            const pathPart = path ? `; path=${path}` : '';
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainPart}${pathPart}`;
          });
        });
      });
      
      sessionStorage.clear();
      localStorage.clear();
      
      // Redirect to home
      window.location.replace('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to delete account. Please try again.');
      setDeleting(false);
      setShowDeleteConfirm(false);
      setConfirmText('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
          <p className="text-red-400">Failed to load account information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
        <p className="text-white/60">
          Manage your account information and preferences.
        </p>
      </motion.div>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#111118] rounded-2xl p-6 border border-white/5 mb-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Profile Information</h2>
            <p className="text-sm text-white/40">Your account details</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-white/60" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white/40 mb-1">Name</p>
              <p className="text-white font-medium">{userData.name || 'Not set'}</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-white/60" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white/40 mb-1">Email</p>
              <p className="text-white font-medium">{userData.email}</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-white/60" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white/40 mb-1">Member Since</p>
              <p className="text-white font-medium">
                {new Date(userData.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {userData.image && (
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <img src={userData.image} alt="Profile" className="w-10 h-10 rounded-lg" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-white/40 mb-1">Profile Picture</p>
                <p className="text-white font-medium">Set via OAuth provider</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Danger Zone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-red-900/10 rounded-2xl p-6 border border-red-500/20"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Danger Zone</h2>
            <p className="text-sm text-white/40">Irreversible and destructive actions</p>
          </div>
        </div>

        <div className="bg-[#0a0a0f] rounded-xl p-4 mb-4">
          <h3 className="font-medium text-white mb-2">Delete Account</h3>
          <p className="text-sm text-white/60 mb-4">
            Once you delete your account, there is no going back. This will permanently delete your
            account, domains, and all associated data. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Account
          </button>
        </div>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#111118] rounded-2xl p-6 max-w-md w-full border border-red-500/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Delete Account?</h3>
            </div>
            <p className="text-white/60 mb-4">
              This action cannot be undone. This will permanently delete your account and all
              associated data including:
            </p>
            <ul className="text-white/60 text-sm mb-4 space-y-1 list-disc list-inside">
              <li>Your account profile</li>
              <li>All whitelisted domains</li>
              <li>All associated data</li>
            </ul>
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-2">
                Type <span className="font-mono text-red-400">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-4 py-2.5 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-red-500/50"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConfirmText('');
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || confirmText !== 'DELETE'}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Account
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
